import moment from "moment-timezone";
import _ from "lodash";

import { VETERAN_STATUS, DISABILITY_STATUS } from "lib/constants";
import { calculateDurationString, isLikelyContractJob } from "lib/util";
import { profileCompletenessData } from "lib/profile";

import Questions from "./questions";

const { Question } = Questions;

const REASON_FOR_LEAVING = {
    NEW_JOB_OPPORTUNITY: 1,
    MOVED: 2,
    SCHOOL: 3,
    HEALTH: 4,
    TIME_OFF: 5,
    SCHEDULE: 6,
    TERMINATED: 7,
    OTHER: 8,
    LAID_OFF: 9,
    CONTRACT_ENDED: 10,
    COMMUTE: 11,
};
const REASON_FOR_LEAVING_LOOKUP = _.invert(REASON_FOR_LEAVING);

const DEGREE_TYPE = {
    HIGH_SCHOOL: 1,
    ASSOCIATES: 2,
    BACHELORS: 3,
    MASTERS: 4,
    OTHER: 5,
    GRADE_SCHOOL: 6,
    TECHNICAL_SCHOOL: 7,
    SOME_COLLEGE: 8,
};
const DEGREE_TYPE_LOOKUP = _.invert(DEGREE_TYPE);

const ETHNICITY = {
    WHITE: 1,
    AFRICAN_AMERICAN: 2,
    HISPANIC_LATINO: 3,
    AMERICAN_INDIAN: 4,
    ASIAN_PACIFIC: 5,
    TWO_OR_MORE: 8,
    OTHER: 6,
    DECLINED: 7,
};
const ETHNICITY_LOOKUP = {
    [ETHNICITY.WHITE]: "White",
    [ETHNICITY.AFRICAN_AMERICAN]: "African American",
    [ETHNICITY.HISPANIC_LATINO]: "Hispanic, Latino",
    [ETHNICITY.AMERICAN_INDIAN]: "Native American",
    [ETHNICITY.ASIAN_PACIFIC]: "Asian / Pacific Islander",
    [ETHNICITY.TWO_OR_MORE]: "2 or more",
    [ETHNICITY.OTHER]: "Other",
    [ETHNICITY.DECLINED]: "Prefer not to say",
};

const VETERAN_STATUS_TEXT = {
    [VETERAN_STATUS.IS_VETERAN]: "I am a protected veteran",
    [VETERAN_STATUS.NOT_VETERAN]: "I am not a protected veteran",
    [VETERAN_STATUS.DECLINED]: "I choose not to self identify",
};

const DISABILITY_STATUS_TEXT = {
    [DISABILITY_STATUS.IS_DISABLED]: "Yes, I have a disability",
    [DISABILITY_STATUS.NOT_DISABLED]: "No, I don’t have a disability",
    [DISABILITY_STATUS.DECLINED]: "I choose not to self identify",
};

const CANDIDATE_SEARCH_STATUS = {
    UNEMPLOYED_ACTIVE: 1,
    EMPLOYED_ACTIVE: 2,
    EMPLOYED_PASSIVE: 3,
    EMPLOYED_INACTIVE: 4,
};

const CANDIDATE_SEARCH_STATUS_MAP = {
    1: "Out of work, and actively looking",
    2: "Employed, but actively looking",
    3: "Employed, but passively looking",
    4: "Not looking",
};

const COMMUTE_TYPE = {
    DRIVING: 2 ** 0,
    TRANSIT: 2 ** 1,
};

const START_TIME = {
    DAY: 2 ** 0,
    SWING: 2 ** 1,
    NIGHT: 2 ** 2,
};

const ALL_SHIFTS = Object.keys(START_TIME).reduce((memo, k) => memo | START_TIME[k], 0);

const SCHEDULE_TYPE_MAP = {
    0: "Full-time",
    1: "Part-time",
    2: "Part-time or Full-time",
};

const SHIFT_PREFERENCE = {
    DEFINITELY_NOT: 1,
    PREFERABLY_NOT: 2,
    OKAY: 3,
    PREFERRED: 4,
};

function formatConstant(str) {
    if (!str) {
        return "";
    }
    return _.startCase(str.replace("_", " ").toLowerCase());
}

function formatAppData(appData) {
    const json = _.cloneDeep(appData);

    json.jobs.forEach((job) => {
        if ("reason" in job) {
            job.reason = _.upperFirst(job.reason);
        } else {
            if (job.reason_for_leaving) {
                // if contract_job property exists, default to that value
                // or compute whether job is likely a contract job
                let contractJob = isLikelyContractJob(job);
                if (Object.prototype.hasOwnProperty.call(job, "contract_job")) {
                    contractJob = job.contract_job;
                }
                job.displayAsContractJob = contractJob;
                job.reason_for_leaving = formatConstant(
                    REASON_FOR_LEAVING_LOOKUP[Number(job.reason_for_leaving)],
                );
            }

            if (job.current) {
                job.end_date = "current";
            }

            job.company = _.startCase(job.company);
            job.city = _.startCase(job.city);
            job.duties = _.upperFirst(job.duties);
            job.durationString = calculateDurationString(job.start_date, job.end_date);
        }
    });

    json.schools.forEach((school) => {
        school.degree_type_string = formatConstant(DEGREE_TYPE_LOOKUP[school.degree_type]);
    });
    json.references = json.jobs.filter((job) => job.manager_reference);

    if (json.user.dob) {
        json.user.birth_date = moment.utc(json.user.dob).format("MM/DD/YYYY");
    } else {
        json.user.birth_date = null;
    }
    json.user.ethnicity = ETHNICITY_LOOKUP[json.user.ethnicity];
    json.user.gender = _.upperFirst(json.user.gender);
    json.user.eligible = Number(json.user.eligible) ? "Yes" : "No";
    if (json.user.disability_status) {
        json.user.disability_status = DISABILITY_STATUS_TEXT[json.user.disability_status];
    }
    if (json.user.veteran_status) {
        json.user.veteran_status = VETERAN_STATUS_TEXT[json.user.veteran_status];
    }

    if (json.user.prefs) {
        json.pref_shift_day = json.user.prefs.shift_day;
        json.pref_shift_swing = json.user.prefs.shift_swing;
        json.pref_shift_night = json.user.prefs.shift_night;
        json.pref_shift_weekend = json.user.prefs.shift_weekend;
        json.pref_shift_overtime = json.user.prefs.shift_overtime;
    }

    json.positions = _.clone(window.positions);
    json.skills = [];

    if (json.pref_shift_starts) {
        json.pref_shift_starts = Object.entries(json.pref_shift_starts)
            .map(([pid, value]) => ({
                position: json.positions.find((p) => p.id === Number(pid)),
                value,
            }))
            .filter((x) => x.value);
    } else {
        json.pref_shift_starts = [];
    }

    const companies = {};
    const skills = [];

    json.questions.forEach((data) => {
        const question = new Question(data);
        if (question.isSkill()) {
            skills.push(question.toJSON());
        } else {
            if (!(data.company_name in companies)) {
                companies[data.company_name] = [];
            }
            companies[data.company_name].push(question.toJSON());
        }
    });

    json.skills = skills;
    json.companies = companies;
    json.profileCompleteness = profileCompletenessData(json);

    return json;
}

export default {
    CANDIDATE_SEARCH_STATUS,
    CANDIDATE_SEARCH_STATUS_MAP,
    COMMUTE_TYPE,
    REASON_FOR_LEAVING_LOOKUP,
    START_TIME,
    ALL_SHIFTS,
    SCHEDULE_TYPE_MAP,
    SHIFT_PREFERENCE,
    formatConstant,
    formatAppData,
};
